import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export function LeadDialog({ trigger, source = "website", defaultNote = "", onSuccess, extraData }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", budget: "", intent: "Buy", note: defaultNote });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, source };
      const extra = typeof extraData === "function" ? extraData() : extraData;
      if (extra) payload.cost_sheet = extra;
      await api.post("/leads", payload);
      toast.success("Thank you! Ayush's team will reach out to you shortly.");
      setForm({ name: "", email: "", phone: "", city: "", budget: "", intent: "Buy", note: defaultNote });
      setOpen(false);
      if (onSuccess) setTimeout(() => onSuccess(extra), 300);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg border-border bg-card" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-extrabold uppercase">Talk to Ayush</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Share your details and get a personalised, no-obligation consultation for your first home.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input required data-testid="lead-name" className="tool-input" placeholder="Full name" value={form.name} onChange={set("name")} />
          <div className="grid grid-cols-2 gap-3">
            <Input required type="email" data-testid="lead-email" className="tool-input" placeholder="Email" value={form.email} onChange={set("email")} />
            <Input required data-testid="lead-phone" className="tool-input" placeholder="Phone" value={form.phone} onChange={set("phone")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="lead-city" className="tool-input" placeholder="City" value={form.city} onChange={set("city")} />
            <Input data-testid="lead-budget" className="tool-input" placeholder="Budget (e.g. 80L)" value={form.budget} onChange={set("budget")} />
          </div>
          <Select value={form.intent} onValueChange={(v) => setForm({ ...form, intent: v })}>
            <SelectTrigger className="tool-input" data-testid="lead-intent"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Buy">Looking to Buy</SelectItem>
              <SelectItem value="Invest">Looking to Invest</SelectItem>
              <SelectItem value="Sell">Looking to Sell</SelectItem>
              <SelectItem value="Advice">Just need advice</SelectItem>
            </SelectContent>
          </Select>
          <Textarea data-testid="lead-note" className="tool-input" placeholder="Anything specific? (optional)" value={form.note} onChange={set("note")} />
          <Button type="submit" disabled={loading} data-testid="lead-submit" className="group w-full rounded-none bg-cyan font-mono text-sm uppercase tracking-wider text-white hover:bg-cyan/90">
            {loading ? "Sending..." : "Request Callback"}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
